import { httpsCallable } from 'firebase/functions';
import type { TenantStatus } from '@kawai/shared';
import { functions } from '../firebase.js';

// ---------- tenantSetStatus (S-71) ----------

export interface TenantSetStatusInput {
  tenantId: string;
  status: TenantStatus;
}

export interface TenantSetStatusResponse {
  ok: true;
  tenantId: string;
  status: TenantStatus;
  contractEndedAt?: string;
  graceUntil?: string;
}

const tenantSetStatusCallable = httpsCallable<
  TenantSetStatusInput,
  TenantSetStatusResponse
>(functions, 'tenantSetStatus');

/**
 * S-71: `tenantSetStatus` Callable の薄いラッパ。
 * Functions 側で saas-admin role / 遷移検証 / 救済期限設定を行い、失敗時は FunctionsError を throw。
 */
export async function callTenantSetStatus(
  input: TenantSetStatusInput,
): Promise<TenantSetStatusResponse> {
  const res = await tenantSetStatusCallable(input);
  return res.data;
}

// ---------- tenantPurge (S-71) ----------

export interface TenantPurgeInput {
  tenantId: string;
  confirmTenantId: string;
}

export interface TenantPurgeResponse {
  ok: true;
  tenantId: string;
  purgedAt: string;
  deleted: Record<string, number>;
  total: number;
}

const tenantPurgeCallable = httpsCallable<TenantPurgeInput, TenantPurgeResponse>(
  functions,
  'tenantPurge',
);

/**
 * S-71: `tenantPurge` Callable の薄いラッパ。
 * Functions 側で saas-admin role / 救済期限経過ゲート / confirmTenantId 照合を行う。
 */
export async function callTenantPurge(
  input: TenantPurgeInput,
): Promise<TenantPurgeResponse> {
  const res = await tenantPurgeCallable(input);
  return res.data;
}

// ---------- エラーメッセージ ----------

interface FunctionsErrorLike {
  code: string;
  message?: string;
}

function isFunctionsErrorLike(err: unknown): err is FunctionsErrorLike {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string' &&
    (err as { code: string }).code.startsWith('functions/')
  );
}

/**
 * S-71 ライフサイクル操作（status 遷移 / purge）のエラーを saas-admin 向け日本語に変換。
 */
export function tenantLifecycleErrorMessage(err: unknown): string {
  if (isFunctionsErrorLike(err)) {
    switch (err.code) {
      case 'functions/failed-precondition':
        return `操作できません：${err.message ?? '前提条件を満たしていません'}`;
      case 'functions/not-found':
        return '対象テナントが見つかりません。';
      case 'functions/invalid-argument':
        return `入力値に誤りがあります：${err.message ?? ''}`;
      case 'functions/permission-denied':
        return '権限がありません（saas-admin 権限が必要です）。';
      case 'functions/unauthenticated':
        return 'セッションが切れました。再ログインしてください。';
      default:
        return `操作に失敗しました（${err.code}）。`;
    }
  }
  if (err instanceof Error) {
    return `操作に失敗しました：${err.message}`;
  }
  return '操作に失敗しました。';
}
